import { Utils, Consts } from '@common';
import { React, Styled, DateFNS } from '@common/3rd';
import { CheckableWidget, DeskComponentRegistry } from '@component';
import {
	CheckableWidgetState,
	DeskDateComponents,
	DeskDateProps,
	DeskTextComponents,
    DeskTextProps,
    DeskTextareaComponents,
    StyledPropsTheme,
    StyledPropsCss
} from '@my-types';
import { StyledComponents } from '@styles';
import { ErrorReminder, PassReminder } from './input-reminder';

class DeskText<
	P extends DeskTextProps,
	S extends CheckableWidgetState,
	C extends DeskTextComponents
	> extends CheckableWidget<P, S, C> {
	textRef: React.RefObject<any>;
	changeDelayHandler?: any;
	timer: any;

	constructor(props: any, context?: any) {
		super(props, context);

		this.textRef = React.createRef();
	}
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-text'
			})`
				display: flex;
				${props => this.renderCSS(props.theme)}
			`,
			Text: Styled(StyledComponents.INPUT)`
				flex-grow: 1;
				border: 1px solid ${props => props.theme.BORDER_COLOR_LIGHT};
				border-radius: ${props => props.theme.BORDER_RADIUS}px;
				line-height: ${props => props.theme.INPUT_LINE_HEIGHT}px;
				padding-left: ${props => props.theme.INPUT_HORIZONTAL_PADDING}px;
				padding-right: ${props => props.theme.INPUT_HORIZONTAL_PADDING}px;
				max-width: ${props => props.theme.INPUT_MAX_WIDTH}px;
				&[disabled],
				&[readOnly],
				&[readonly] {
					// background-color: transparent; 
				}
				&::placeholder {
					opacity: 0.7;
				}
				${props => this.paintColor(props.theme)}

				/* 获取焦点的时候, 如果有错误信息, 显示 */
				:focus + i > span {
					opacity: 1;
					animation-name: none;
				}
			`,
			SuffixText: Styled.div.attrs({
				'data-widget': 'desk-suffix-text'
			})`
				margin-left: 12px;
				color: gray;
				opacity: 0.8;
			`
		} as C;
	}
	renderReminder(): JSX.Element | void {
		if (this.isCheckFailOnWhatever()) {
			// 有校验失败信息
			return <ErrorReminder msg={this.getCheckFailMessage()!} />;
		} else if (this.isCheckedAnyway()) {
			// 没有校验信息, 但是曾经校验过
			return <PassReminder />;
		}
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				<C.Text
					type="text"
					onChange={this.onTextChanged}
					onFocus={this.onTextFocus}
					onBlur={this.onTextBlur}
					ref={this.textRef}
					readOnly={!this.isEditable()}
					defaultValue={this.getValueFromModel()}
                    placeholder={this.getPlaceholder()}
                    maxLength={this.getMaxlength()}
					id={this.getId()}
					autoComplete="new-password"
				/>
				{this.renderReminder()}
				<C.SuffixText>{this.getSuffixText()}</C.SuffixText>
			</C.Box>
		);
	}
	protected paintColor(theme: StyledPropsTheme): string {
		return this.isCheckFailOnWhatever() ? `color: ${theme.COLOR_ERROR};margin-bottom:30px;` : '';
	}
	focus(): this {
		this.textRef.current.focus();
		return this;
	}
	protected setValueToDOM(value: any, propName?: string): this {
		if (propName == null || propName === this.getPropName()) {
			if (this.textRef.current) {
				this.textRef.current.value = value;
			}
			// this.checkData();
		}
		return this;
	}
	protected doAfterCheckCompleted(): this {
		if (this.props.labellingParent) {
			this.props.labellingParent.onCheckCompleted(!this.isCheckFailOnWhatever());
		}
		return this;
	}
	protected onTextChanged = (): void => {
		const value = this.textRef.current.value;
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}
		this.handleValueChange(() => this.setValueToModel(value).checkData());
		// this.setValueToModel(value).checkData();
	};
	protected onTextFocus = (): void => {
		clearTimeout(this.timer);
		this.textRef.current!.select();
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusGained();
		}
	};
	protected onTextBlur = (): void => {
		if (this.props.labellingParent) {
			if (this.props.labellingParent.onFocusLost) {
				this.props.labellingParent.onFocusLost();
			}
		}
	};
	protected getSuffixText(): string | undefined {
		return this.props.suffixText as string | undefined;
	}
	protected getPlaceholder(): string {
		return this.props.placeholder as string;
    }
    protected getMaxlength(): number {
        return this.props.maxLength as number;
	}
	protected getId(): string {
		return this.props.id as string || '';
	}
	protected handleValueChange(delayFunc: () => void): void {
		if (this.changeDelayHandler) {
			clearTimeout(this.changeDelayHandler);
			delete this.changeDelayHandler;
		}
		this.changeDelayHandler = setTimeout(() => {
			delayFunc.call(this);
			delete this.changeDelayHandler;
		}, 300);
	}
}
class DeskTextarea<
	P extends DeskTextProps,
	S extends CheckableWidgetState,
	C extends DeskTextareaComponents
	> extends CheckableWidget<P, S, C> {
	textRef: React.RefObject<any>;

	constructor(props: any, context?: any) {
		super(props, context);

		this.textRef = React.createRef();
	}
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-textarea'
			})`
				display: flex;
				${props => this.renderCSS(props.theme)}
			`,
			Textarea: Styled.textarea`
				display: block;
				position: relative;
				outline: none;
				margin: 0;
				resize:none;
				background-color: ${props => props.theme.INPUT_BACKGRAOUND_COLOR};
				color: ${props => props.theme.FONT_COLOR};
				font-family: ${props => props.theme.FONT_FAMILY};
				font-weight: ${props => props.theme.FONT_WEIGHT};
				font-size: ${props => props.theme.FONT_SIZE}px;
				padding: ${props => props.theme.INPUT_VERTICAL_PADDING}px ${props =>
				props.theme.INPUT_HORIZONTAL_PADDING}px;
				min-height: ${props => props.theme.INPUT_HEIGHT * 2}px;
				appearance: none;
				text-overflow: ellipsis;
				white-space: nowrap;
				overflow-x: hidden;
				${(props: StyledPropsCss) =>
				props.css ? props.css(props, (props as any).theme) : ''}}
				flex-grow: 1;
				border: 1px solid ${props => props.theme.BORDER_COLOR_LIGHT};
				border-radius: ${props => props.theme.BORDER_RADIUS}px;
				line-height: ${props => props.theme.INPUT_LINE_HEIGHT}px;
				padding-left: ${props => props.theme.INPUT_HORIZONTAL_PADDING}px;
				padding-right: ${props => props.theme.INPUT_HORIZONTAL_PADDING}px;
				max-width: ${props => props.theme.INPUT_MAX_WIDTH}px;
				&[disabled],
				&[readOnly],
				&[readonly] {
					// background-color: transparent; 
				}
				${props => this.paintColor(props.theme)}
				& + i > span {
					top: 120px;
				}
				/* 获取焦点的时候, 如果有错误信息, 显示 */
				:focus + i > span {
					opacity: 1;
					animation-name: none;
				}
			`
		} as C;
	}
	renderReminder(): JSX.Element | void {
		if (this.isCheckFailOnWhatever()) {
			// 有校验失败信息
			return <ErrorReminder msg={this.getCheckFailMessage()!} />;
		} else if (this.isCheckedAnyway()) {
			// 没有校验信息, 但是曾经校验过
			return <PassReminder />;
		}
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				<C.Textarea
					onChange={this.onTextChanged}
					onFocus={this.onTextFocus}
                    onBlur={this.onTextBlur}
                    rows={this.props.rows}
					ref={this.textRef}
					readOnly={!this.isEditable()}
					maxLength={this.getMaxlength()}
					defaultValue={this.getValueFromModel()}
				/>
				{this.renderReminder()}
			</C.Box>
		);
	}
	protected getMaxlength(): number {
		return this.props.maxLength as number;
	}
	protected paintColor(theme: StyledPropsTheme): string {
		return this.isCheckFailOnWhatever() ? `color: ${theme.COLOR_ERROR};margin-bottom:30px;` : '';
	}
	focus(): this {
		this.textRef.current.focus();
		return this;
	}
	protected setValueToDOM(value: any, propName?: string): this {
		if (propName == null || propName === this.getPropName()) {
			this.textRef.current.value = value;
			// this.checkData();
		}
		return this;
	}
	protected doAfterCheckCompleted(): this {
		if (this.props.labellingParent) {
			this.props.labellingParent.onCheckCompleted(!this.isCheckFailOnWhatever());
		}
		return this;
	}
	protected onTextChanged = (): void => {
		const value = this.textRef.current.value;
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}

		this.setValueToModel(value).checkData();
	};
	protected onTextFocus = (): void => {
		this.textRef.current!.select();
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusGained();
		}
	};
	protected onTextBlur = (): void => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusLost();
		}
	};
}
class DeskPwd<P extends DeskTextProps, S extends CheckableWidgetState, C extends DeskTextComponents> extends DeskText<
	P,
	S,
	C
	> {
	protected initComponents(): C {
		const C = super.initComponents();
		C.Box = Styled(C.Box).attrs({
			'data-widget': 'desk-pwd'
		})``;
		return C;
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				<C.Text
					type="password"
					onChange={this.onTextChanged}
					onFocus={this.onTextFocus}
					onBlur={this.onTextBlur}
					ref={this.textRef}
					readOnly={!this.isEditable()}
					placeholder={this.getPlaceholder()}
                    maxLength={this.getMaxlength()}
					defaultValue={this.getValueFromModel()}
					autoComplete="new-password"
				/>
				{this.renderReminder()}
			</C.Box>
		);
	}
	protected onTextChanged = (): void => {
		const value = this.textRef.current.value;
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}

		this.setValueToModel(value);
	};
}

class DeskDate<P extends DeskDateProps, S extends CheckableWidgetState, C extends DeskDateComponents> extends DeskText<
	P,
	S,
	C
	> {
	labelRef: React.RefObject<any>;

	constructor(props: any, context?: any) {
		super(props, context);
		this.labelRef = React.createRef();
	}

	componentDidMount() {
		super.componentDidMount();
		this.forceUpdate();
	}
	protected initComponents(): C {
		const C = super.initComponents();
		C.Box = Styled(C.Box).attrs({
			'data-widget': 'desk-date'
		})``;
		C.Text = Styled(C.Text)`
			opacity: 0;
		`;
		C.DisplayLabel = Styled.span`
			display: block;
			position: absolute;
			height: ${props => props.theme.INPUT_HEIGHT}px;
			// width: 100%;
			border: 1px solid ${props => props.theme.BORDER_COLOR_LIGHT};
			border-radius: ${props => props.theme.BORDER_RADIUS}px;
			padding-left: ${props => props.theme.INPUT_HORIZONTAL_PADDING}px;
			pointer-events: none;
			max-width: ${props => props.theme.INPUT_MAX_WIDTH}px;
			${props => {
				const cText = document.getElementsByClassName(C.Text.styledComponentId);
				// console.log(cText);
				if (cText.length > 0) {
					const elem = cText[0] as Element;
					//@ts-ignore
					const elemWidth = elem.offsetWidth;
					// console.log(elemWidth);
					return `width:${elemWidth}px;`
				} else {
					return `width:100%;`
				}
			}}
			${props => this.renderDisplayTextCSS(props.theme)}
		`;
		C.DisplayLabelCaret = Styled(StyledComponents.FAR)`
			display: block;
			position: absolute;
			top: 0;
			right: 0;
			width: ${props => props.theme.INPUT_LINE_HEIGHT}px;
			height: ${props => props.theme.INPUT_HEIGHT}px;
			line-height: ${props => props.theme.INPUT_HEIGHT}px;
			color: ${props => props.theme.DATE_CARET_COLOR};
			font-size: ${props => props.theme.DATE_CARET_SIZE}px;
			cursor: pointer;
		` as any;
		return C;
	}
	protected renderDisplayTextCSS(theme: StyledPropsTheme): string {
		if (this.isCheckedAnyway()) {
			// return `width: calc(100% - ${theme.INPUT_LINE_HEIGHT}px);`;
			return '';
		} else {
			return '';
		}
	}
	protected renderDisplayText(): JSX.Element {
		const C = this.getComponents();
		const DisplayLabelCaret = C.DisplayLabelCaret as any;
		return (
			<C.DisplayLabel ref={this.labelRef}>
				{this.getValueFromModel()}
				<DisplayLabelCaret icon="calendar" />
			</C.DisplayLabel>
		);
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				<C.Text
					type="date"
					onChange={this.onTextChanged}
					onFocus={this.onTextFocus}
					onBlur={this.onTextBlur}
					ref={this.textRef}
					readOnly={!this.isEditable()}
					defaultValue={this.asDOMAccepted(this.getValueFromModel())}
					id={this.getId()}
				/>
				{this.renderDisplayText()}
				{this.renderReminder()}
				<C.SuffixText>{this.getSuffixText()}</C.SuffixText>
			</C.Box>
		);
	}
	protected paintColor(theme: StyledPropsTheme): string {
		const css = super.paintColor(theme);

		if (this.isCheckFailOnWhatever()) {
			return `${css} + span, + span > i {color: ${theme.COLOR_ERROR};}`;
		} else if (!this.isEditable()) {
			return `${css} + span > i {color: ${theme.COLOR_DISABLED};}`;
		} else {
			const focus = document.activeElement === this.textRef.current;
			return focus ? `${css} + span > i {color: ${theme.COLOR_PRIMARY};}` : css;
		}
	}
	protected asDOMAccepted(value?: string | null): string {
		// HTML 标准要求必须是横杠(-), 参考RFC3339
		return value ? this.formatValue(value, Consts.W3C_DATE_FORMAT) : '';
	}
	protected getFormat(): string {
		return (this.props.format || Consts.W3C_DATE_FORMAT) as string;
	}
	protected formatValue(value?: string | null, format?: string): string {
		let displayValue = '';
		if (value != null && !Utils.isEmpty(value)) {
			displayValue = DateFNS.format(value, format || this.getFormat());
		}
		return displayValue;
	}
	protected setValueToDOM(value: any, propName?: string): this {
		if (propName == null || propName === this.getPropName()) {
			this.textRef.current.value = this.asDOMAccepted(value);
			// this.checkData();
			this.forceUpdate();
		}
		return this;
	}
	protected getValueFromModel(propName?: string): any {
		const value = super.getValueFromModel(propName);
		if (!propName || propName === this.getPropName()) {
			return this.formatValue(value);
		} else {
			return value;
		}
	}
	protected setValueToModel(value: any, propName?: string): this {
		if (!propName || propName === this.getPropName()) {
			return super.setValueToModel(this.formatValue(value, Consts.STANDARD_DATETIME_FORMAT), propName);
		} else {
			return super.setValueToModel(value, propName);
		}
	}
	protected onTextBlur = (): void => {
		const value = this.textRef.current.value;
		this.setValueToModel(value).checkData();
		if (this.props.labellingParent) {
			if (this.props.labellingParent.onFocusLost) {
				this.props.labellingParent.onFocusLost();
			}
		}
	};
	protected onTextChanged = (): void => {
		const value = this.textRef.current.value;
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}

		this.setValueToModel(value).checkData();
	};
}

class DeskSuffixText<P extends DeskTextProps, S extends CheckableWidgetState, C extends DeskTextComponents> extends DeskText<
	P,
	S,
	C
	> {
	protected initComponents(): C {
		const C = super.initComponents();
		C.Box = Styled(C.Box).attrs({
			'data-widget': 'desk-suffix-text'
		})`
			display: flex;
			flex-direction: row;
			> span {
				margin-left: 4px;
			}
		`
		return C;
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				<C.Text
					type="text"
					onChange={this.onTextChanged}
					onFocus={this.onTextFocus}
					onBlur={this.onTextBlur}
					ref={this.textRef}
					readOnly={!this.isEditable()}
					defaultValue={this.getValueFromModel()}
					placeholder={this.getPlaceholder()}
                    maxLength={this.getMaxlength()}
					autoComplete="new-password"
				/>
				{this.renderSuffix()}
				{this.renderReminder()}
			</C.Box>
		);
	}
	protected onTextChanged = (): void => {
		const value = this.textRef.current.value;
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}

		this.setValueToModel(value).checkData();
	};

	protected renderSuffix(): JSX.Element {
		return <span>{this.getSuffix()}</span>;
	}

	private getSuffix() {
		return this.props.suffix;
	}
}

DeskComponentRegistry.register({
	text: DeskText,
	pwd: DeskPwd,
	date: DeskDate,
    suffixText: DeskSuffixText,
    textarea: DeskTextarea
});

export { DeskText, DeskPwd, DeskDate, DeskSuffixText, DeskTextarea };
